import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AxiosError } from 'axios';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { environment } from 'src/environments/environment';
import { PayeeHeaderComponent } from '../../../shared/payee-header/payee-header.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { GoogleSsoButtonComponent } from '../../../shared/google-sso-button/google-sso-button.component';
import { FooterSimpleComponent } from '../../../shared/footer-simple/footer-simple.component';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  imports: [
    PayeeHeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    PayeeButtonComponent,
    RouterLink,
    GoogleSsoButtonComponent,
    FooterSimpleComponent,
    TranslateModule,
  ],
})
export class RegisterComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private utmSource?: string;
  private utmCampaign?: string;
  private utmContent?: string;
  private utmMedium?: string;

  readonly registerForm = this.fb.group({
    email: this.fb.control('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
  });

  readonly loading = signal(false);

  ngOnInit() {
    const queryParams: Record<string, string> = this.route.snapshot.queryParams;

    this.utmCampaign = queryParams.utm_campaign;
    this.utmSource = queryParams.utm_source;
    this.utmContent = queryParams.utm_content;
    this.utmMedium = queryParams.utm_medium;
  }

  get email() {
    return this.registerForm.controls.email;
  }

  get googleSSOUrl(): string {
    return `${environment.baseUrl}/api/auth/sso/google/redirect`;
  }

  readonly register = async () => {
    if (this.registerForm.invalid || this.loading()) {
      this.email.markAsDirty();
      return;
    }
    try {
      this.loading.set(true);
      await this.authService.register({
        email: this.email.value,
        is_privacy_accepted: true,
        is_terms_accepted: true,
        utm_campaign: this.utmCampaign,
        utm_source: this.utmSource,
        utm_content: this.utmContent,
        utm_medium: this.utmMedium,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (
          error.response?.data?.errors?.email?.includes(
            'The email has already been taken.',
          )
        ) {
          this.email.setErrors({ used: true });
          return;
        }
        this.registerForm.setErrors({
          unknown:
            error.response?.data?.message ??
            this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        });
      } else {
        console.error('Unknown error during signup', error);
      }
    } finally {
      this.loading.set(false);
    }
  };
}
